<template>
  <div class="refund-bill page--refund-bill">
    <div class="page__header">
      <div class="page__title">
        <router-link :to="backLinkRoute" class="page__back-link">
          <arrow-button
            class="page__back-link-icon"
            :rotated180="true"
          ></arrow-button>
        </router-link>
        <div class="page__title-text">
          {{ $t('refund-bill.page.title') }}
        </div>
      </div>
    </div>
    <div class="refund-bill__content">
      <div class="refund-bill__form">
        <div class="refund-bill__form-fields">
          <div class="refund-bill__form-field">
            <text-input
              v-model:value="refundData.sum"
              :mask="$options.NUMBER_MASK"
              :placeholder="$t('refund-bill.fields.sum-placeholder')"
              :title="$t('refund-bill.fields.sum')"
            ></text-input>
            <div class="refund-bill__form-field-hint">
              {{ $t('refund-bill.fields.sum-hint') }}
            </div>
          </div>
          <div class="refund-bill__form-field">
            <text-input
              v-model:value="refundData.comment"
              :title="$t('refund-bill.fields.comment')"
              :placeholder="$t('refund-bill.fields.comment-placeholder')"
            ></text-input>
          </div>
          <div class="refund-bill__form-field">
            <text-input
              v-model:value="refundData.pan"
              :disabled="true"
              :mask="'XXXX XXXX XXXX XXXX'"
              :title="$t('refund-bill.fields.pan')"
              :placeholder="$t('refund-bill.fields.pan-placeholder')"
            ></text-input>
          </div>
        </div>
        <div class="refund-bill__form-actions">
          <primary-button
            class="refund-bill__form-action"
            :disabled="!isRefundButtonActive"
            :label="$t('refund-bill.actions.refund')"
            @click="refundBill"
          ></primary-button>
        </div>
      </div>
      <div class="refund-bill__history" v-if="bill.refunds.length > 0">
        <div class="refund-bill__history-title">
          {{ $t('refund-bill.history.title') }}
        </div>
        <div class="refund-bill__history-table">
          <company-bill-refunds-table
            :refunds="bill.refunds"
          ></company-bill-refunds-table>
        </div>
      </div>
    </div>
    <success-notification
      ref="billRefundSuccessNotification"
      :text="$t('refund-bill.notifications.success')"
    ></success-notification>
  </div>
</template>

<script>
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import CustomSelect from '@vue-app/components/base/input/CustomSelect.vue'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import moment from 'moment'
import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import CompanyBillRefundsTable from '@vue-app/components/company/bills/table/refund/CompanyBillRefundsTable.vue'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import { Bill } from '@js-app/entities/Bill'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'

export default {
  name: 'RefundBill',
  props: ['id', 'billId'],
  components: {
    SuccessNotification,
    CompanyBillRefundsTable,
    CustomSelect,
    PrimaryButton,
    TextInput,
    ArrowButton,
  },
  data() {
    return {
      refundData: {
        currency: 'uah',
        sum: '',
        comment: '',
        pan: '',
      },
      bill: new Bill(),
      billRefunding: false,
      billLoading: true,
      backLinkRoute: '',
    }
  },

  NUMBER_MASK: createNumberMask({
    prefix: '',
    allowDecimal: true,
    includeThousandsSeparator: false,
    allowNegative: false,
    decimalLimit: 2,
  }),

  created() {
    this.loadBill()
  },

  computed: {
    isRefundButtonActive() {
      return (
        !this.billRefunding &&
        parseFloat(this.refundData.sum) > 0 &&
        !this.billLoading &&
        (this.bill.canRefund || this.bill.canCancelPreAuth)
      )
    },
  },

  methods: {
    async loadBill() {
      this.billLoading = true

      let _billResponse = await CompaniesApi.getBill(this.id, this.billId)

      this.bill = new Bill(_billResponse.data.ReportOperations[0])

      this.refundData.sum = this.bill.sum
      this.refundData.pan = this.bill.pan

      this.billLoading = false
    },
    disabledDateHandler(val1) {
      return moment().diff(moment(val1), 'days') > 0
    },
    async refundBill() {
      if (this.billRefunding) {
        return false
      }

      this.billRefunding = true

      let _refundBillResponse = null

      try {
        _refundBillResponse = await CompaniesApi.makeBillRefund(
          this.id,
          this.bill.operationId,
          this.refundData.sum
        )

        this.$refs.billRefundSuccessNotification.show()
        await this.loadBill()
      } catch (e) {
        //TODO: Show error toast with error info
        return
      } finally {
        this.billRefunding = false
      }

      // await this.$router.push(RouteHelper.getRoute("bill-view", {
      //   companyId: this.id,
      //   billId: _refundBillResponse.data.BillID
      // }));
    },
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.backLinkRoute =
        from.path !== '/'
          ? from.path
          : RouteHelper.getRoute('bill-view', {
              companyId: to.params.id,
              billId: to.params.billId,
            })
    })
    // this.backLinkRoute = from || RouteHelper.getRoute("bill-view", {companyId: this.id, billId: this.billId});
  },
}
</script>

<style lang="scss" scoped>
.page {
  &--refund-bill {
    padding-top: 28px;
  }

  &__header {
    margin-bottom: 24px;
  }
}
.refund-bill {
  &__content {
    display: grid;
    grid-template-columns: 390px 1fr;
    column-gap: 15px;
  }

  &__form {
    background: #ffffff;
    border-radius: 22px;
    padding: 60px 50px 74px;
    width: 100%;
    max-width: 390px;

    &-fields {
      margin-bottom: 40px;
    }

    &-field {
      margin-bottom: 25px;

      &-hint {
        width: 100%;
        font-weight: 500;
        font-size: 10px;
        line-height: 14px;
        color: #b9c4d8;
        margin-top: 5px;
        flex-wrap: wrap;
      }

      &--row {
        display: flex;
        align-items: stretch;
        flex-wrap: wrap;
      }

      &-small {
        width: 95px;
        margin-right: 26px;
        flex-shrink: 0;
        flex-grow: 0;
      }

      &-big {
        width: calc(100% - 121px);
        flex-shrink: 1;
        flex-grow: 1;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &-action {
      padding: 25px 25px;
    }
  }

  &__history {
    &-title {
      background: #ffffff;
      border-radius: 8px;
      padding: 7px 28px 8px;
      font-weight: 600;
      font-size: 16px;
      line-height: 28px;
      color: #465a8b;
    }

    &-table {
      margin-top: 15px;
    }
  }
}
</style>
